import { FormGroupState } from '@shared/models/common';
import { MonEtablissementForm } from '@features/compte/mon-etablissement/features/mon-etablissement-detail/models/mon-Etablissement-form';
import { Action, createReducer, on } from '@ngrx/store';
import { MonEtablissementPageActions } from '@features/compte/mon-etablissement/features/mon-etablissement-detail/store/actions/mon-etablissement.actions';
import { updateObject } from '@shared/functions';
import { Iban } from '@shared/models/referentiel/ibans';

export const featureKey = 'monEtablissementForm';

const IBAN_STATUT_ORDER = ['Actif', 'En cours de validation', 'Inactif'];

const getIbanStatutRank = (statut?: string): number => {
  const index = IBAN_STATUT_ORDER.indexOf(statut ?? '');
  return index === -1 ? IBAN_STATUT_ORDER.length : index;
};

const insertIbanAtStatutGroupStart = (ibans: Iban[], iban: Iban): Iban[] => {
  const withoutIban = ibans.filter((existingIban) => existingIban.iban !== iban.iban);
  const rank = getIbanStatutRank(iban.statut);
  const insertIndex = withoutIban.findIndex((existingIban) => getIbanStatutRank(existingIban.statut) >= rank);

  return insertIndex === -1
    ? [...withoutIban, iban]
    : [...withoutIban.slice(0, insertIndex), iban, ...withoutIban.slice(insertIndex)];
};

export interface MonEtablissementState extends FormGroupState<MonEtablissementForm> {
  isLoading: boolean;
  ibans: Iban[];
  ibansLoading: boolean;
  ibansErrorMessage: string | null;
  addIbanLoading: boolean;
  addIbanErrorMessage: string | null;
  addIbanSuccess: boolean;
}

export const initialState: MonEtablissementState = {
  value: new MonEtablissementForm(),
  checkingForm: false,
  isValid: false,
  errorMessage: null,
  errors: [],
  isLoading: false,
  ibans: [],
  ibansLoading: false,
  ibansErrorMessage: null,
  addIbanLoading: false,
  addIbanErrorMessage: null,
  addIbanSuccess: false,
};

export const MonEtablissementReducer = createReducer(
  initialState,
  on(MonEtablissementPageActions.updateForm, (state, { form }) =>
    updateObject(state, { value: form }),
  ),
  on(MonEtablissementPageActions.resetForm, (state, { form }) =>
    updateObject(state, { value: form }),
  ),
  on(MonEtablissementPageActions.updateLoading, (state, { isLoading }) => ({
    ...state,
    isLoading,
  })),
  on(MonEtablissementPageActions.loadIbans, (state) => ({
    ...state,
    ibans: [],
    ibansLoading: true,
    ibansErrorMessage: null,
  })),
  on(MonEtablissementPageActions.loadIbansSuccess, (state, { ibans }) => ({
    ...state,
    ibans,
    ibansLoading: false,
  })),
  on(MonEtablissementPageActions.loadIbansFailure, (state, { errorMessage }) => ({
    ...state,
    ibans: [],
    ibansLoading: false,
    ibansErrorMessage: errorMessage,
  })),
  on(MonEtablissementPageActions.addIban, (state) => ({
    ...state,
    addIbanLoading: true,
    addIbanErrorMessage: null,
    addIbanSuccess: false,
  })),
  on(MonEtablissementPageActions.addIbanSuccess, (state, { iban }) => ({
    ...state,
    ibans: insertIbanAtStatutGroupStart(state.ibans, iban),
    addIbanLoading: false,
    addIbanErrorMessage: null,
    addIbanSuccess: true,
  })),
  on(MonEtablissementPageActions.addIbanFailure, (state, { errorMessage }) => ({
    ...state,
    addIbanLoading: false,
    addIbanErrorMessage: errorMessage,
    addIbanSuccess: false,
  })),
  on(MonEtablissementPageActions.resetAddIbanState, (state) => ({
    ...state,
    addIbanLoading: false,
    addIbanErrorMessage: null,
    addIbanSuccess: false,
  })),
);
export const reducer = (
  state: MonEtablissementState | undefined,
  action: Action,
): MonEtablissementState => MonEtablissementReducer(state, action);

export const getFormValue = (state: MonEtablissementState) => state.value;
